import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import api from '../../api';
import MarkEntryDashboard from './MarkEntryDashboard';

// ============================================================
// UPDATED: MarkEntryStudentList with batchId support
// ============================================================
const MarkEntryStudentList = ({ 
  assignmentId: propAssignmentId, 
  assignment: propAssignment,
  subjectId, 
  subjectName, 
  classId, 
  componentId, 
  itemId 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // ✅ Extract batchId, batchNumber, readOnly and isEndExam from navigation state
  const batchId = location.state?.batchId || null;
  const batchNumber = location.state?.batchNumber || null;
  const readOnly = location.state?.readOnly || false;
  const isEndExam = location.state?.isEndExam || false;
  
  console.log('[MarkEntryStudentList] 🔍 Received from navigation state:', {
    batchId,
    batchNumber,
    readOnly,
    isEndExam,
    assignmentId: propAssignmentId,
    subjectId,
    classId
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [, setAssignment] = useState(propAssignment);
  const [finalAssignmentId, setFinalAssignmentId] = useState(propAssignmentId);
  const [, setTotalMaxMarks] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  
  const [academicContext, setAcademicContext] = useState({
    academicYearId: null,
    classId: classId,
    subjectId: subjectId
  });

  // Function to refresh data after successful upload
  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  useEffect(() => {
    const fetchAssignmentDetails = async () => {
      if (propAssignment && propAssignment.isFinalized) {
        setAssignment(propAssignment);
        setFinalAssignmentId(propAssignment._id);
        setTotalMaxMarks(propAssignment.totalMaxMarks || 0);
        
        setAcademicContext({
          academicYearId: propAssignment.academicYearId,
          classId: propAssignment.classId || classId,
          subjectId: propAssignment.subjectId || subjectId
        });
        
        setLoading(false);
        return;
      }

      if (propAssignmentId && !propAssignment) {
        try {
          // Use appropriate API based on isEndExam flag
          const endpoint = isEndExam 
            ? `/api/faculty/end-exam-paper-templates/${propAssignmentId}`
            : `/api/faculty/paper-template-assignment/${propAssignmentId}`;
          
          const response = await api.get(endpoint);
          if (response.data.success) {
            const fetchedAssignment = response.data.assignment;
            setAssignment(fetchedAssignment);
            setFinalAssignmentId(fetchedAssignment._id);
            setTotalMaxMarks(fetchedAssignment.totalMaxMarks || 0);
            
            setAcademicContext({
              academicYearId: fetchedAssignment.academicYearId,
              classId: fetchedAssignment.classId || classId,
              subjectId: fetchedAssignment.subjectId || subjectId
            });
            
            setLoading(false);
            return;
          }
        } catch (err) {
          console.error('Error fetching assignment:', err);
        }
      }

      if (!propAssignment && !propAssignmentId && componentId && classId && subjectId) {
        try {
          const params = { componentId, classId, subjectId };
          if (itemId && itemId !== 'null') {
            params.itemId = itemId;
          }
          
          // Use appropriate API based on isEndExam flag
          const endpoint = isEndExam 
            ? '/api/faculty/end-exam-paper-templates/assignment'
            : '/api/faculty/paper-template-assignment';
          
          const response = await api.get(endpoint, { params });
          if (response.data.success && response.data.assignment) {
            const fetchedAssignment = response.data.assignment;
            setAssignment(fetchedAssignment);
            setFinalAssignmentId(fetchedAssignment._id);
            setTotalMaxMarks(fetchedAssignment.totalMaxMarks || 0);
            
            setAcademicContext({
              academicYearId: fetchedAssignment.academicYearId,
              classId: fetchedAssignment.classId || classId,
              subjectId: fetchedAssignment.subjectId || subjectId
            });
            
            setLoading(false);
            return;
          }
        } catch (err) {
          console.error('Error fetching assignment by params:', err);
        }
      }

      setError('No assessment structure found. Please configure the paper template first.');
      setLoading(false);
    };

    fetchAssignmentDetails();
  }, [propAssignmentId, propAssignment, componentId, classId, subjectId, itemId, isEndExam]);

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white px-6 py-6 rounded-xl shadow-xs border border-gray-100 flex items-center gap-3 max-w-xs w-full">
          <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
          <p className="text-gray-600 font-semibold text-xs tracking-wide">Loading assessment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 p-4 font-sans flex items-center justify-center">
        <div className="bg-white rounded-xl border border-gray-200/80 shadow-xs p-5 max-w-md w-full text-center">
          <AlertCircle className="w-6 h-6 text-rose-500 mx-auto mb-2" />
          <h2 className="text-sm font-bold text-gray-900 mb-1">Configuration Missing</h2>
          <p className="text-gray-500 text-xs mb-4 leading-relaxed">{error}</p>
          <button
            onClick={handleBack}
            className="w-full py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!finalAssignmentId) {
    return (
      <div className="min-h-screen bg-slate-50 p-4 font-sans flex items-center justify-center">
        <div className="bg-white rounded-xl border border-gray-200/80 shadow-xs p-5 max-w-md w-full text-center">
          <AlertCircle className="w-6 h-6 text-amber-500 mx-auto mb-2" />
          <h2 className="text-sm font-bold text-gray-900 mb-1">Assignment Not Finalized</h2>
          <p className="text-gray-500 text-xs mb-4 leading-relaxed">
            Please finalize the paper template before entering marks.
          </p>
          <button
            onClick={handleBack}
            className="w-full py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans antialiased pb-6">
      <div className="max-w-7xl mx-auto px-1.5 sm:px-4 py-3">
        <MarkEntryDashboard 
          key={refreshKey}
          assignmentId={finalAssignmentId}
          classId={academicContext.classId}
          subjectId={academicContext.subjectId}
          academicYearId={academicContext.academicYearId}
          batchId={batchId}           // ✅ PASS BATCH ID
          batchNumber={batchNumber}   // ✅ PASS BATCH NUMBER
          onRefreshRequest={handleRefresh}
          readOnly={readOnly}
          isEndExam={isEndExam}
        />
      </div>
    </div>
  );
};

export default MarkEntryStudentList;